import { useMemo } from 'react';
import type { WireHistoryPoint } from '@shared/contract';

/**
 * Probe history strip.
 *
 * Bar height encodes latency, colour encodes status. A failed probe has no
 * meaningful latency, so it renders full-height in its status colour rather
 * than as a zero-height gap that would read as "fast".
 */
export function Sparkline({ history, checkKey }: { history: WireHistoryPoint[]; checkKey: string }) {
  const max = useMemo(() => {
    const successful = history.filter((h) => h.status === 'ok' && h.latencyMs != null);
    return Math.max(60, ...successful.map((h) => h.latencyMs as number));
  }, [history]);

  if (!history.length) {
    return <div className="spark__empty">awaiting first probe…</div>;
  }

  return (
    <div className="spark" role="img" aria-label={`Last ${history.length} probes for ${checkKey}`}>
      {history.map((point) => {
        const height =
          point.status === 'ok' && point.latencyMs != null
            ? Math.max(12, Math.round((point.latencyMs / max) * 100))
            : 100;
        const detail = point.latencyMs != null ? ` · ${point.latencyMs}ms` : '';
        return (
          <span
            key={point.ts}
            className={`spark__bar is-${point.status}`}
            style={{ height: `${height}%` }}
            title={`${new Date(point.ts).toLocaleTimeString()} — ${point.status}${detail}`}
          />
        );
      })}
    </div>
  );
}
